// shared-components/services/ChunkedUploadService.ts
// Phase 3A Enhancement: Upload Resilience & Recovery

import { supabase } from "@shared/core/services/SupabaseService";
import {
  networkStatusService,
  networkSpeedMonitor,
  performanceMetricsService,
} from "@shared/services";
import {
  uploadSessionStorage,
  type StoredUploadSession,
} from "./UploadSessionStorage";
import { retryWithBackoff } from "@shared/universal";
import {
  ConnectionQuality,
  UploadResponse,
  ExtendedError,
} from "../types/ServiceTypes";

interface ChunkedUploadOptions {
  websiteId: string;
  productId: string;
  fileType: "tracks" | "previews";
  productName?: string;
  artist?: string;
  trackTitle?: string;
  trackNumber?: number;
  isAlbum?: boolean;
  targetFilename?: string; // Optional target filename for meaningful naming
  chunkSize?: number; // Default 10MB, will be overridden by dynamic sizing
  onProgress?: (uploadedBytes: number, totalBytes: number) => void;
  onChunkProgress?: (chunkIndex: number, totalChunks: number) => void;
  // Phase 3A: Upload Resilience & Recovery options
  enablePersistence?: boolean; // Default true - persist sessions for resume
  autoRetryOnNetwork?: boolean; // Default true - auto-retry on network recovery
  onNetworkStatusChange?: (isOnline: boolean, quality: string) => void;
  onSessionPersisted?: (sessionId: string) => void;
  onUploadPaused?: (reason: string) => void;
  onUploadResumed?: () => void;
  // Phase 3D: Rate limiting options
  sequentialMode?: boolean; // Default false - upload chunks sequentially instead of concurrently
  delayBetweenChunks?: number; // Default 0 - delay in ms between chunk uploads (for rate limiting)
}

interface ChunkedUploadResult {
  success: boolean;
  path: string;
  filename: string;
  size: number;
  sessionId: string;
}

interface UploadSession {
  sessionId: string;
  fileName: string;
  totalChunks: number;
  chunkSize: number;
  uploadedChunks: Set<number>;
  totalBytes: number;
  uploadedBytes: number;
  // Phase 3A: Upload Resilience & Recovery fields
  createdAt: number;
  lastActivity: number;
  isPersisted: boolean;
  isPaused: boolean;
  pauseReason?: string;
  networkQuality?: string;
  adaptiveChunkSize: number; // Current chunk size based on network conditions
  file?: File; // Store reference to original file for resume
  options?: ChunkedUploadOptions; // Store options for resume
}

class ChunkedUploadService {
  private activeSessions: Map<string, UploadSession> = new Map();
  private defaultChunkSize = 10 * 1024 * 1024; // 10MB
  private maxRetries = 3;

  // Phase 3A: Upload Resilience & Recovery properties
  private networkEventUnsubscribe: (() => void) | null = null;
  private pausedSessions: Set<string> = new Set();
  private sessionCleanupInterval: NodeJS.Timeout | null = null;

  // Phase 3B: Performance optimization properties
  private uploadMetricsMap = new Map<string, string>(); // sessionId -> uploadId mapping

  constructor() {
    this.initializeNetworkMonitoring();
    this.startSessionCleanup();
  }

  // Get Krystal API URL
  private getKrystalApiUrl(): string {
    return import.meta.env.VITE_KRYSTAL_API_URL || "https://nyesservices.co.uk";
  }

  // Get Supabase token for authentication
  private async getSupabaseToken(): Promise<string> {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token || "";
  }

  // Generate unique session ID
  private generateSessionId(): string {
    return `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Phase 3A: Initialize network monitoring
  private initializeNetworkMonitoring(): void {
    this.networkEventUnsubscribe = networkStatusService.addEventListener(
      (event) => {
        switch (event.type) {
          case "offline":
            this.pauseAllActiveSessions("Network disconnected");
            break;
          case "online":
            this.resumeAllPausedSessions();
            break;
          case "quality-change":
            this.adjustChunkSizesForAllSessions();
            break;
          case "slow-connection":
            this.pauseAllActiveSessions("Connection too slow for upload");
            break;
        }
      },
    );
  }

  // Phase 3A: Start periodic session cleanup
  private startSessionCleanup(): void {
    // Clean up every 5 minutes
    this.sessionCleanupInterval = setInterval(
      async () => {
        await this.cleanupAbandonedSessions();
      },
      5 * 60 * 1000,
    );
  }

  // Phase 3A: Pause all active sessions
  private pauseAllActiveSessions(reason: string): void {
    for (const [sessionId, session] of this.activeSessions) {
      if (!session.isPaused) {
        this.pauseSession(sessionId, reason);
      }
    }
  }

  // Phase 3A: Resume all paused sessions
  private resumeAllPausedSessions(): void {
    for (const sessionId of this.pausedSessions) {
      this.resumeSession(sessionId);
    }
  }

  // Phase 3B: Enhanced adaptive chunk sizing using NetworkSpeedMonitor
  private adjustChunkSizesForAllSessions(): void {
    const networkPrediction = networkSpeedMonitor.getPrediction();
    const recommendedSize = networkPrediction.recommendedChunkSize;

    for (const session of this.activeSessions.values()) {
      // Only adjust if NetworkSpeedMonitor recommends adaptation
      if (networkSpeedMonitor.shouldAdaptChunkSize(session.adaptiveChunkSize)) {
        session.adaptiveChunkSize = recommendedSize;
      }
    }
  }

  // Check browser memory before starting upload
  private checkMemoryAvailability(fileSize: number): boolean {
    if (performance.memory) {
      const memoryInfo = performance.memory;
      const usedMemory = memoryInfo.usedJSHeapSize;
      const memoryLimit = memoryInfo.jsHeapSizeLimit;
      const availableMemory = memoryLimit - usedMemory;

      // Ensure we have at least 2x the chunk size available
      const requiredMemory = this.defaultChunkSize * 2;

      if (availableMemory < requiredMemory) {
        console.warn(
          `Insufficient memory: ${availableMemory} bytes available, ${requiredMemory} bytes required`,
        );
        return false;
      }
    }

    return true;
  }

  // Split file into chunks while preserving MIME type
  private async createFileChunks(
    file: File,
    chunkSize: number,
  ): Promise<Blob[]> {
    const chunks: Blob[] = [];
    let offset = 0;

    while (offset < file.size) {
      const end = Math.min(offset + chunkSize, file.size);
      // Preserve the original file's MIME type for server validation
      const chunk = file.slice(offset, end, file.type);
      chunks.push(chunk);
      offset = end;
    }

    return chunks;
  }

  // Phase 3B: Enhanced upload chunk with NetworkSpeedMonitor integration and performance tracking
  private async uploadChunk(
    chunk: Blob,
    chunkIndex: number,
    sessionId: string,
    fileName: string,
    totalChunks: number,
    options: ChunkedUploadOptions,
  ): Promise<boolean> {
    const session = this.activeSessions.get(sessionId);

    // Check if session is paused
    if (session?.isPaused) {
      throw new Error(
        `Upload paused: ${session.pauseReason || "Unknown reason"}`,
      );
    }

    // Use enhanced retry logic from retryWithBackoff
    return retryWithBackoff(
      async () => {
        const startTime = Date.now();

        const formData = new FormData();
        // Ensure chunk has proper MIME type for server validation
        const chunkBlob =
          chunk instanceof Blob
            ? chunk
            : new Blob([chunk], { type: "application/zip" }); // Default to ZIP for album uploads

        formData.append("chunk", chunkBlob, `${fileName}.chunk.${chunkIndex}`);
        formData.append("sessionId", sessionId);
        formData.append("chunkIndex", chunkIndex.toString());
        formData.append("totalChunks", totalChunks.toString());
        formData.append("fileName", fileName);
        formData.append("websiteId", options.websiteId);
        formData.append("productId", options.productId);
        formData.append("fileType", options.fileType);

        // Add optional metadata
        if (options.productName)
          formData.append("productName", options.productName);
        if (options.artist) formData.append("artist", options.artist);
        if (options.trackTitle)
          formData.append("trackTitle", options.trackTitle);
        if (options.trackNumber)
          formData.append("trackNumber", options.trackNumber.toString());
        if (options.isAlbum !== undefined)
          formData.append("isAlbum", options.isAlbum.toString());

        const response = await fetch(
          `${this.getKrystalApiUrl()}/api/upload/chunked`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${await this.getSupabaseToken()}`,
            },
            body: formData,
          },
        );

        if (!response.ok) {
          const error = await response
            .json()
            .catch(() => ({ error: `HTTP ${response.status}` }));
          const uploadError: ExtendedError = new Error(
            error.error || `Upload failed: ${response.status}`,
          );
          uploadError.status = response.status;
          throw uploadError;
        }

        const result = await response.json();
        const endTime = Date.now();

        // Phase 3B: Enhanced performance tracking with NetworkSpeedMonitor
        const duration = endTime - startTime;

        // Record speed measurement for adaptive chunking
        networkSpeedMonitor.recordSpeedMeasurement(
          chunk.size,
          duration,
          chunk.size,
        );

        // Also record with NetworkStatusService for compatibility
        networkStatusService.recordBandwidthMeasurement(chunk.size, duration);

        // Record chunk metrics for performance analytics
        const uploadId = this.uploadMetricsMap.get(sessionId);
        if (uploadId) {
          await performanceMetricsService.recordChunkMetric({
            uploadId,
            chunkIndex,
            chunkSize: chunk.size,
            uploadTimeMs: duration,
            retryAttempts: 0, // Will be updated by retry logic
            speed: (chunk.size / duration) * 1000, // bytes per second
            timestamp: Date.now(),
          });
        }

        // Update session with latest activity
        if (session) {
          session.lastActivity = Date.now();
          session.networkQuality =
            networkStatusService.getConnectionQuality().level;

          // Persist session state
          if (options.enablePersistence !== false) {
            await this.persistSession(session);
          }
        }

        // Call progress callback
        if (options.onChunkProgress) {
          options.onChunkProgress(chunkIndex + 1, totalChunks);
        }

        // Return the result object so we can capture completion info
        return result;
      },
      {
        maxRetries: this.maxRetries,
        initialDelay: 1000,
        maxDelay: 10000,
        onRetry: (_info) => {
          // Retry handled by retryWithBackoff
        },
        shouldRetry: (error: unknown) => {
          // Don't retry if session is paused or if it's a client error
          if (session?.isPaused) return false;
          if (error.status && error.status >= 400 && error.status < 500)
            return false;
          return true;
        },
      },
    );
  }

  // Phase 3A: Enhanced main upload method with session persistence and resume capability
  async uploadFile(
    file: File,
    options: ChunkedUploadOptions,
  ): Promise<ChunkedUploadResult> {
    // Validate file size (max 1GB to match Krystal API limit)
    const maxFileSize = 1024 * 1024 * 1024; // 1GB
    if (file.size > maxFileSize) {
      throw new Error(
        `File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds maximum allowed size (1GB)`,
      );
    }

    // Check memory availability
    if (!this.checkMemoryAvailability(file.size)) {
      throw new Error(
        "Insufficient browser memory for file upload. Please close other tabs or try a smaller file.",
      );
    }

    // Check network suitability
    if (!networkStatusService.isSuitableForLargeUploads()) {
      const message = "Network connection not suitable for large uploads";
      if (options.onUploadPaused) {
        options.onUploadPaused(message);
      }
      throw new Error(message);
    }

    // Phase 3B: Use NetworkSpeedMonitor for superior adaptive chunk sizing
    let adaptiveChunkSize: number;
    let connectionQuality: ConnectionQuality;

    const speedPrediction = networkSpeedMonitor.getPrediction(file.size);
    if (speedPrediction.confidence > 0.6) {
      // Use NetworkSpeedMonitor recommendation if confident
      adaptiveChunkSize = speedPrediction.recommendedChunkSize;
      // Still get network quality for session tracking
      connectionQuality = networkStatusService.getConnectionQuality();
    } else {
      // Fallback to NetworkStatusService
      connectionQuality = networkStatusService.getConnectionQuality();
      adaptiveChunkSize = connectionQuality.recommendedChunkSize;
    }

    const chunkSize = options.chunkSize || adaptiveChunkSize;
    const sessionId = this.generateSessionId();

    // Start performance metrics tracking
    const uploadId = performanceMetricsService.recordUploadStart(
      sessionId,
      file.name,
      file.size,
    );
    this.uploadMetricsMap.set(sessionId, uploadId);

    try {
      // Create file chunks
      const chunks = await this.createFileChunks(file, chunkSize);
      const totalChunks = chunks.length;

      // Create enhanced session tracking
      const session: UploadSession = {
        sessionId,
        fileName: file.name,
        totalChunks,
        chunkSize,
        uploadedChunks: new Set(),
        totalBytes: file.size,
        uploadedBytes: 0,
        // Phase 3A: New fields
        createdAt: Date.now(),
        lastActivity: Date.now(),
        isPersisted: false,
        isPaused: false,
        networkQuality: connectionQuality.level,
        adaptiveChunkSize: chunkSize,
        file: file, // Store file reference for resume
        options: options, // Store options for resume
      };

      this.activeSessions.set(sessionId, session);

      // Persist session if enabled
      if (options.enablePersistence !== false) {
        await this.persistSession(session);
        if (options.onSessionPersisted) {
          options.onSessionPersisted(sessionId);
        }
      }

      // Upload chunks - use sequential mode if requested to avoid rate limiting
      // Capture the server response which contains the actual filename
      let completionResponse = null;
      if (options.sequentialMode) {
        completionResponse = await this.uploadChunksSequentially(
          chunks,
          session,
          sessionId,
          file.name,
          totalChunks,
          options,
        );
      } else {
        completionResponse = await this.uploadChunksConcurrently(
          chunks,
          session,
          sessionId,
          file.name,
          totalChunks,
          options,
          connectionQuality.level,
        );
      }

      // Phase 3B: Record upload completion metrics
      const uploadDuration = Date.now() - session.createdAt;
      const averageSpeed = (file.size / uploadDuration) * 1000; // bytes per second
      const retryCount = 0; // TODO: Track retries across chunks
      const networkQuality = networkStatusService.getConnectionQuality();

      await performanceMetricsService.recordUploadComplete(
        uploadId,
        true, // success
        uploadDuration,
        averageSpeed,
        totalChunks,
        chunkSize,
        retryCount,
        undefined, // no error
        networkQuality.level,
      );

      // Mark session as completed and clean up
      if (options.enablePersistence !== false) {
        const storedSession = await this.convertToStoredSession(session);
        storedSession.status = "completed";
        await uploadSessionStorage.saveSession(storedSession);
      }

      this.activeSessions.delete(sessionId);
      this.uploadMetricsMap.delete(sessionId);

      // Use server response if available, otherwise fall back to client data
      const serverPath = completionResponse?.path;
      const serverFilename = completionResponse?.filename;

      // Use targetFilename if provided for meaningful naming, otherwise fallback to file.name
      const finalFilename =
        options.targetFilename || serverFilename || file.name;
      const finalPath =
        serverPath || `music/${options.fileType}/${finalFilename}`;

      return {
        success: true,
        path: finalPath,
        filename: finalFilename,
        size: file.size,
        sessionId,
        url: finalPath, // Ensure URL matches path
      };
    } catch (error) {
      // Phase 3B: Record upload failure metrics
      const session = this.activeSessions.get(sessionId);
      if (session) {
        const uploadDuration = Date.now() - session.createdAt;
        const averageSpeed =
          session.uploadedBytes > 0
            ? (session.uploadedBytes / uploadDuration) * 1000
            : 0;
        const networkQuality = networkStatusService.getConnectionQuality();

        await performanceMetricsService.recordUploadComplete(
          uploadId,
          false, // failed
          uploadDuration,
          averageSpeed,
          session.totalChunks,
          session.chunkSize,
          0, // TODO: Track retries
          (error as Error).message,
          networkQuality.level,
        );
      }

      // Update session status on error
      if (session && options.enablePersistence !== false) {
        const storedSession = await this.convertToStoredSession(session);
        storedSession.status = "failed";
        await uploadSessionStorage.saveSession(storedSession);
      }

      // Clean up session on error
      this.activeSessions.delete(sessionId);
      this.uploadMetricsMap.delete(sessionId);
      console.error("Chunked upload failed:", error);
      throw error;
    }
  }

  // Phase 3D: Sequential upload method for rate limiting
  private async uploadChunksSequentially(
    chunks: Blob[],
    session: UploadSession,
    sessionId: string,
    fileName: string,
    totalChunks: number,
    options: ChunkedUploadOptions,
  ): Promise<UploadResponse> {
    let completionResponse = null;

    for (let i = 0; i < totalChunks; i++) {
      // Skip already uploaded chunks (for resume functionality)
      if (session.uploadedChunks.has(i)) {
        continue;
      }

      // Check if upload was paused
      if (session.isPaused) {
        throw new Error(`Upload paused: ${session.pauseReason}`);
      }

      const chunkResult = await this.uploadChunk(
        chunks[i],
        i,
        sessionId,
        fileName,
        totalChunks,
        options,
      );

      // Update session progress
      session.uploadedChunks.add(i);
      session.uploadedBytes += chunks[i].size;
      session.lastActivity = Date.now();

      // Check if this chunk completed the upload (server returns complete file info)
      if (chunkResult && chunkResult.path && chunkResult.filename) {
        completionResponse = chunkResult;
      }

      // Call progress callback
      if (options.onProgress) {
        options.onProgress(session.uploadedBytes, session.totalBytes);
      }

      // Add delay between chunks if specified (for rate limiting)
      if (
        options.delayBetweenChunks &&
        options.delayBetweenChunks > 0 &&
        i < totalChunks - 1
      ) {
        await new Promise((resolve) =>
          setTimeout(resolve, options.delayBetweenChunks),
        );
      }
    }

    return completionResponse;
  }

  // Phase 3D: Concurrent upload method (existing behavior)
  private async uploadChunksConcurrently(
    chunks: Blob[],
    session: UploadSession,
    sessionId: string,
    fileName: string,
    totalChunks: number,
    options: ChunkedUploadOptions,
    connectionQuality: string,
  ): Promise<UploadResponse> {
    const maxConcurrentUploads = this.getOptimalConcurrency(connectionQuality);

    let completionResponse = null;

    for (let i = 0; i < totalChunks; i += maxConcurrentUploads) {
      const batchPromises = [];

      for (let j = 0; j < maxConcurrentUploads && i + j < totalChunks; j++) {
        const chunkIndex = i + j;

        // Skip already uploaded chunks (for resume functionality)
        if (session.uploadedChunks.has(chunkIndex)) {
          continue;
        }

        const chunkPromise = this.uploadChunk(
          chunks[chunkIndex],
          chunkIndex,
          sessionId,
          fileName,
          totalChunks,
          options,
        )
          .then((chunkResult) => {
            // Update session progress
            session.uploadedChunks.add(chunkIndex);
            session.uploadedBytes += chunks[chunkIndex].size;
            session.lastActivity = Date.now();

            // Check if this chunk completed the upload (server returns complete file info)
            if (chunkResult && chunkResult.path && chunkResult.filename) {
              completionResponse = chunkResult;
            }

            // Call progress callback
            if (options.onProgress) {
              options.onProgress(session.uploadedBytes, session.totalBytes);
            }
          })
          .catch((error) => {
            if (error.message.includes("paused")) {
              // Session was paused, don't reject the entire upload
              return Promise.resolve(); // Continue with other chunks
            }

            throw error;
          });

        batchPromises.push(chunkPromise);
      }

      // Wait for this batch to complete before starting the next
      if (batchPromises.length > 0) {
        await Promise.all(batchPromises);
      }

      // Check if upload was paused
      if (session.isPaused) {
        throw new Error(`Upload paused: ${session.pauseReason}`);
      }
    }

    return completionResponse;
  }

  // Phase 3A: Get optimal concurrency based on network quality
  private getOptimalConcurrency(quality: string): number {
    switch (quality) {
      case "poor":
        return 1;
      case "fair":
        return 2;
      case "good":
        return 3;
      case "excellent":
        return 4;
      default:
        return 3;
    }
  }

  // Check if file should use chunked upload (files > 50MB)
  shouldUseChunkedUpload(file: File): boolean {
    const threshold = 50 * 1024 * 1024; // 50MB
    return file.size > threshold;
  }

  // Get upload progress for a session
  getUploadProgress(
    sessionId: string,
  ): { uploadedBytes: number; totalBytes: number; percentage: number } | null {
    const session = this.activeSessions.get(sessionId);
    if (!session) return null;

    const percentage =
      session.totalBytes > 0
        ? (session.uploadedBytes / session.totalBytes) * 100
        : 0;

    return {
      uploadedBytes: session.uploadedBytes,
      totalBytes: session.totalBytes,
      percentage: Math.round(percentage * 100) / 100,
    };
  }

  // Cancel an upload session
  async cancelUpload(sessionId: string): Promise<boolean> {
    const session = this.activeSessions.get(sessionId);
    if (!session) return false;

    // Clean up session
    this.activeSessions.delete(sessionId);

    return true;
  }

  // Get list of active upload sessions
  getActiveSessions(): string[] {
    return Array.from(this.activeSessions.keys());
  }

  // Phase 3A: New methods for upload resilience and recovery

  /**
   * Resume an upload from a stored session
   */
  async resumeUpload(sessionId: string): Promise<ChunkedUploadResult> {
    // Check if session is already active
    if (this.activeSessions.has(sessionId)) {
      throw new Error(`Session ${sessionId} is already active`);
    }

    // Load session from storage
    const storedSession = await uploadSessionStorage.loadSession(sessionId);
    if (!storedSession) {
      throw new Error(`Session ${sessionId} not found in storage`);
    }

    if (storedSession.status === "completed") {
      throw new Error(`Session ${sessionId} is already completed`);
    }

    // Convert back to active session
    const session = await this.convertFromStoredSession(storedSession);

    // Validate file still exists and matches
    if (!session.file) {
      throw new Error("Original file not available for resume");
    }

    // Re-validate file
    if (
      session.file.size !== storedSession.fileMetadata.size ||
      session.file.lastModified !== storedSession.fileMetadata.lastModified
    ) {
      throw new Error("File has changed since upload started");
    }

    // Restore session to active sessions
    this.activeSessions.set(sessionId, session);
    session.isPaused = false;
    session.lastActivity = Date.now();

    // Resume the upload using the existing uploadFile logic
    return this.continueUpload(session);
  }

  /**
   * Continue an upload from where it left off
   */
  private async continueUpload(
    session: UploadSession,
  ): Promise<ChunkedUploadResult> {
    const file = session.file!;
    const options = session.options!;

    try {
      // Create file chunks
      const chunks = await this.createFileChunks(
        file,
        session.adaptiveChunkSize,
      );
      const totalChunks = chunks.length;

      // Upload remaining chunks with adaptive concurrency
      const networkQuality = networkStatusService.getConnectionQuality();
      const maxConcurrentUploads = this.getOptimalConcurrency(
        networkQuality.level,
      );

      for (let i = 0; i < totalChunks; i += maxConcurrentUploads) {
        const batchPromises = [];

        for (let j = 0; j < maxConcurrentUploads && i + j < totalChunks; j++) {
          const chunkIndex = i + j;

          // Skip already uploaded chunks
          if (session.uploadedChunks.has(chunkIndex)) {
            continue;
          }

          const chunkPromise = this.uploadChunk(
            chunks[chunkIndex],
            chunkIndex,
            session.sessionId,
            file.name,
            totalChunks,
            options,
          ).then(() => {
            // Update session progress
            session.uploadedChunks.add(chunkIndex);
            session.uploadedBytes += chunks[chunkIndex].size;
            session.lastActivity = Date.now();

            // Call progress callback
            if (options.onProgress) {
              options.onProgress(session.uploadedBytes, session.totalBytes);
            }
          });

          batchPromises.push(chunkPromise);
        }

        // Wait for this batch to complete before starting the next
        if (batchPromises.length > 0) {
          await Promise.all(batchPromises);
        }

        // Check if upload was paused
        if (session.isPaused) {
          throw new Error(`Upload paused: ${session.pauseReason}`);
        }
      }

      // Mark session as completed
      if (options.enablePersistence !== false) {
        const storedSession = await this.convertToStoredSession(session);
        storedSession.status = "completed";
        await uploadSessionStorage.saveSession(storedSession);
      }

      this.activeSessions.delete(session.sessionId);

      // Use targetFilename if provided for meaningful naming
      const finalFilename = options.targetFilename || file.name;
      const finalPath = `music/${options.fileType}/${finalFilename}`;

      return {
        success: true,
        path: finalPath,
        filename: finalFilename,
        size: file.size,
        sessionId: session.sessionId,
      };
    } catch (error) {
      // Update session status on error
      if (options.enablePersistence !== false) {
        const storedSession = await this.convertToStoredSession(session);
        storedSession.status = "failed";
        await uploadSessionStorage.saveSession(storedSession);
      }

      this.activeSessions.delete(session.sessionId);
      throw error;
    }
  }

  /**
   * Pause an active upload session
   */
  pauseSession(sessionId: string, reason?: string): boolean {
    const session = this.activeSessions.get(sessionId);
    if (!session) {
      console.warn(`Cannot pause session ${sessionId}: not found`);
      return false;
    }

    session.isPaused = true;
    session.pauseReason = reason || "Manually paused";
    this.pausedSessions.add(sessionId);

    // Notify via callback
    if (session.options?.onUploadPaused) {
      session.options.onUploadPaused(session.pauseReason);
    }

    return true;
  }

  /**
   * Resume a paused upload session
   */
  resumeSession(sessionId: string): boolean {
    const session = this.activeSessions.get(sessionId);
    if (!session) {
      console.warn(`Cannot resume session ${sessionId}: not found`);
      return false;
    }

    if (!session.isPaused) {
      return true;
    }

    session.isPaused = false;
    session.pauseReason = undefined;
    this.pausedSessions.delete(sessionId);

    // Notify via callback
    if (session.options?.onUploadResumed) {
      session.options.onUploadResumed();
    }

    return true;
  }

  /**
   * Get recoverable upload sessions
   */
  async getRecoverableSessions(): Promise<StoredUploadSession[]> {
    return uploadSessionStorage.getRecoverableSessions();
  }

  /**
   * Clean up abandoned sessions
   */
  async cleanupAbandonedSessions(): Promise<number> {
    return uploadSessionStorage.cleanupExpiredSessions();
  }

  /**
   * Convert UploadSession to StoredUploadSession for persistence
   */
  private async convertToStoredSession(
    session: UploadSession,
  ): Promise<StoredUploadSession> {
    return {
      sessionId: session.sessionId,
      fileName: session.fileName,
      totalChunks: session.totalChunks,
      chunkSize: session.chunkSize,
      uploadedChunks: Array.from(session.uploadedChunks),
      totalBytes: session.totalBytes,
      uploadedBytes: session.uploadedBytes,
      createdAt: session.createdAt,
      lastActivity: session.lastActivity,
      fileMetadata: session.file
        ? {
            type: session.file.type,
            size: session.file.size,
            lastModified: session.file.lastModified,
          }
        : { type: "", size: session.totalBytes, lastModified: Date.now() },
      uploadOptions: session.options
        ? {
            websiteId: session.options.websiteId,
            productId: session.options.productId,
            fileType: session.options.fileType,
            productName: session.options.productName,
            artist: session.options.artist,
          }
        : { websiteId: "", productId: "", fileType: "tracks" },
      status: session.isPaused ? "active" : "active",
    };
  }

  /**
   * Convert StoredUploadSession back to UploadSession for resume
   */
  private async convertFromStoredSession(
    storedSession: StoredUploadSession,
  ): Promise<UploadSession> {
    // Note: File reference will need to be provided separately for resume
    return {
      sessionId: storedSession.sessionId,
      fileName: storedSession.fileName,
      totalChunks: storedSession.totalChunks,
      chunkSize: storedSession.chunkSize,
      uploadedChunks: new Set(storedSession.uploadedChunks),
      totalBytes: storedSession.totalBytes,
      uploadedBytes: storedSession.uploadedBytes,
      createdAt: storedSession.createdAt,
      lastActivity: storedSession.lastActivity,
      isPersisted: true,
      isPaused: false,
      networkQuality: networkStatusService.getConnectionQuality().level,
      adaptiveChunkSize: storedSession.chunkSize,
      // Note: File and options will need to be set when resuming
    };
  }

  /**
   * Persist session to storage
   */
  private async persistSession(session: UploadSession): Promise<void> {
    try {
      const storedSession = await this.convertToStoredSession(session);
      const saved = await uploadSessionStorage.saveSession(storedSession);
      if (saved) {
        session.isPersisted = true;
      } else {
        console.warn(`Failed to persist session ${session.sessionId}`);
      }
    } catch (error) {
      console.error(`Error persisting session ${session.sessionId}:`, error);
    }
  }

  /**
   * Check if a file can be resumed
   */
  async canResumeFile(file: File): Promise<string | null> {
    const recoverableSessions = await this.getRecoverableSessions();

    for (const session of recoverableSessions) {
      if (
        session.fileName === file.name &&
        session.fileMetadata.size === file.size &&
        Math.abs(session.fileMetadata.lastModified - file.lastModified) < 1000
      ) {
        return session.sessionId;
      }
    }

    return null;
  }

  /**
   * Get network status information
   */
  getNetworkStatus() {
    return {
      isOnline: networkStatusService.getNetworkMetrics().isOnline,
      quality: networkStatusService.getConnectionQuality(),
      isSuitableForUploads: networkStatusService.isSuitableForLargeUploads(),
    };
  }

  /**
   * Destroy service and clean up resources
   */
  destroy(): void {
    // Clean up network event listener
    if (this.networkEventUnsubscribe) {
      this.networkEventUnsubscribe();
      this.networkEventUnsubscribe = null;
    }

    // Clear session cleanup interval
    if (this.sessionCleanupInterval) {
      clearInterval(this.sessionCleanupInterval);
      this.sessionCleanupInterval = null;
    }

    // Clear active sessions
    this.activeSessions.clear();
    this.pausedSessions.clear();
  }
}

export const chunkedUploadService = new ChunkedUploadService();
